/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

import { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Pagination,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import {
  useGetStudioMembersQuery,
  useChangeMemberRoleMutation,
  useRemoveMemberMutation,
} from '../../../features/studios/studiosApi';
import {
  StudioMembersTabProps,
  MemberStatus,
} from '../../../types/studioMembers';
import { StudioRole } from '../../../types/studio';
import { useStudioPermissions } from '../../../hooks/useStudioPermissions';
import { useStudioUserLimits } from '../../../hooks/useSubscriptionLimits';
import { useGetCurrentSubscriptionQuery } from '../../../features/auth/authApi';
import ConfirmDialog from '../../../features/classEssentials/components/ConfirmDialog';
import { getApiErrorMessage } from '../../../utils/errorHandling';
import MemberSearchBar from './MemberSearchBar';
import MemberTable from './MemberTable';
import UsageIndicator from '../../subscription/UsageIndicator';

const StudioMembersTab = ({ studioId }: StudioMembersTabProps) => {
  const { t } = useTranslation('studio');
  const navigate = useNavigate();

  // Get permissions and role to conditionally show management features
  const { permissions, role } = useStudioPermissions();
  const isStudioOwner = role === StudioRole.OWNER;

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<StudioRole | undefined>();
  const [selectedStatus, setSelectedStatus] = useState<
    MemberStatus | undefined
  >();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // API queries and mutations
  const {
    data: membersResponse,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetStudioMembersQuery({
    studioId,
    search: searchTerm,
    role: selectedRole,
    status: selectedStatus,
    page: currentPage,
    pageSize,
  });

  // Get total active member count (unfiltered) for usage calculation
  // We need to make a separate query without filters to get the actual total
  const { data: totalMembersResponse } = useGetStudioMembersQuery({
    studioId,
    search: '', // No search filter for total count
    role: undefined, // No role filter for total count
    status: MemberStatus.ACTIVE, // Only count active members
    page: 1,
    pageSize: 1, // We only need the count, not the actual members
  });

  const currentMemberCount = totalMembersResponse?.count || 0;
  const { canAddUser, isAtUserLimit, userLimitPercent } = useStudioUserLimits(
    studioId,
    currentMemberCount
  );

  // Get subscription data for actual member limit
  const { data: subscription } = useGetCurrentSubscriptionQuery();
  const maxUsers = subscription?.plan.max_users_per_studio || 0;

  const [changeMemberRole, { isLoading: isChangingRole }] =
    useChangeMemberRoleMutation();
  const [removeMember, { isLoading: isRemovingMember }] =
    useRemoveMemberMutation();

  // Role-change/remove failure shown in a Snackbar
  const [actionError, setActionError] = useState<string | null>(null);
  // Change waiting for the "Change to Owner" confirmation
  const [pendingOwnerChange, setPendingOwnerChange] = useState<{
    membershipId: string;
    reason?: string;
  } | null>(null);

  // Event handlers
  const handleSearchChange = useCallback((search: string) => {
    setSearchTerm(search);
    setCurrentPage(1); // Reset to first page when searching
  }, []);

  const handleRoleFilter = useCallback((filterRole?: StudioRole) => {
    setSelectedRole(filterRole);
    setCurrentPage(1);
  }, []);

  const handleStatusFilter = useCallback((status?: MemberStatus) => {
    setSelectedStatus(status);
    setCurrentPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedRole(undefined);
    setSelectedStatus(undefined);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback(
    (_: React.ChangeEvent<unknown>, page: number) => {
      setCurrentPage(page);
    },
    []
  );

  const applyRoleChange = useCallback(
    async (membershipId: string, newRole: StudioRole, reason?: string) => {
      try {
        await changeMemberRole({
          membershipId,
          toRole: newRole,
          reason: reason || 'Role changed via member management',
        }).unwrap();
        // Role change successful - the table will automatically update via RTK Query
      } catch (err) {
        setActionError(
          getApiErrorMessage(err, t('membersTab.roleChangeError'))
        );
      }
    },
    [changeMemberRole, t]
  );

  const handleRoleChange = useCallback(
    async (membershipId: string, newRole: StudioRole, reason?: string) => {
      // An owner can't be changed or removed from this page, so confirm first
      if (newRole === StudioRole.OWNER) {
        setPendingOwnerChange({ membershipId, reason });
        return;
      }
      await applyRoleChange(membershipId, newRole, reason);
    },
    [applyRoleChange]
  );

  const handleConfirmOwnerChange = useCallback(async () => {
    if (!pendingOwnerChange) return;
    const { membershipId, reason } = pendingOwnerChange;
    setPendingOwnerChange(null);
    await applyRoleChange(membershipId, StudioRole.OWNER, reason);
  }, [pendingOwnerChange, applyRoleChange]);

  const handleRemoveMember = useCallback(
    async (membershipId: string, reason?: string) => {
      try {
        await removeMember({ membershipId, reason }).unwrap();
        // Member removal successful - the table will automatically update via RTK Query
      } catch (err) {
        setActionError(getApiErrorMessage(err, t('membersTab.removeError')));
      }
    },
    [removeMember, t]
  );

  const handleSendMessage = useCallback((userId: string) => {
    // TODO: Open message dialog or navigate to messaging
    console.log('Send message to user:', userId);
  }, []);

  const handleInviteMembers = useCallback(() => {
    // Check if user can add members before proceeding
    if (!canAddUser) {
      console.warn('Cannot add members: subscription limit reached');
      return;
    }
    navigate(`/studio/${studioId}/invitations`);
  }, [navigate, studioId, canAddUser]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // Calculate total pages
  const totalPages = membersResponse
    ? Math.ceil(membersResponse.count / pageSize)
    : 0;
  const members = membersResponse?.results || [];
  const pendingOwnerMember = pendingOwnerChange
    ? members.find(member => member.id === pendingOwnerChange.membershipId)
    : undefined;
  const pendingOwnerName =
    pendingOwnerMember?.display_name ||
    pendingOwnerMember?.user?.full_name ||
    pendingOwnerMember?.email ||
    '';

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" component="h2" gutterBottom>
            {t('membersTab.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('membersTab.subtitle')}
          </Typography>
          {/* Member Usage Indicator */}
          <Box sx={{ mt: 1 }}>
            <UsageIndicator
              type="members"
              current={currentMemberCount}
              limit={maxUsers}
              showProgress
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={isLoading}
          >
            {t('membersTab.refresh')}
          </Button>
          {permissions?.canManageMembers && (
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={handleInviteMembers}
              disabled={!canAddUser}
              color={!canAddUser ? 'error' : 'primary'}
              sx={{
                ...(!canAddUser && {
                  '&:disabled': {
                    backgroundColor: 'error.light',
                    color: 'error.contrastText',
                  },
                }),
              }}
            >
              {!canAddUser
                ? t('membersTab.memberLimitReached')
                : t('membersTab.inviteMembers')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Member Limit Warning - Only show to studio owners */}
      {!isAtUserLimit && userLimitPercent >= 80 && isStudioOwner && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>{t('membersTab.approachingLimit')}</strong>{' '}
          {t('membersTab.approachingLimitMessage', {
            percent: Math.round(userLimitPercent),
          })}
          {userLimitPercent >= 95 && t('membersTab.approachingLimitUpgrade')}
        </Alert>
      )}

      {/* Member Limit Reached Alert - Only show to studio owners */}
      {isAtUserLimit && isStudioOwner && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <strong>{t('membersTab.memberLimitReached')}</strong>{' '}
          {t('membersTab.limitReachedMessage')}{' '}
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => navigate('/pricing')}
            sx={{ ml: 1 }}
          >
            {t('membersTab.upgradePlan')}
          </Button>
        </Alert>
      )}

      {/* Search and Filters */}
      <MemberSearchBar
        searchTerm={searchTerm}
        selectedRole={selectedRole}
        selectedStatus={selectedStatus}
        onSearchChange={handleSearchChange}
        onRoleFilter={handleRoleFilter}
        onStatusFilter={handleStatusFilter}
        onClearFilters={handleClearFilters}
      />

      {/* Error State */}
      {isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {t('membersTab.loadError')}
          {error && 'data' in error && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {JSON.stringify(error.data)}
            </Typography>
          )}
        </Alert>
      )}

      {/* Loading State for Actions */}
      {(isChangingRole || isRemovingMember) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            p: 2,
            bgcolor: 'action.hover',
            borderRadius: 1,
          }}
        >
          <CircularProgress size={16} />
          <Typography variant="body2">
            {isChangingRole && t('membersTab.changingRole')}
            {isRemovingMember && t('membersTab.removingMember')}
          </Typography>
        </Box>
      )}

      {/* Members Table */}
      <MemberTable
        members={members}
        loading={isLoading}
        onRoleChange={handleRoleChange}
        onRemoveMember={handleRemoveMember}
        onSendMessage={handleSendMessage}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 4,
          }}
        >
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Members Count */}
      {membersResponse && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('membersTab.showingResults', {
              current: members.length,
              total: membersResponse.count,
            })}
          </Typography>
        </Box>
      )}

      <ConfirmDialog
        open={!!pendingOwnerChange}
        title={t('membersTab.ownerConfirm.title')}
        message={t('membersTab.ownerConfirm.message', {
          name: pendingOwnerName,
        })}
        confirmText={t('membersTab.ownerConfirm.confirm')}
        cancelText={t('membersTab.ownerConfirm.cancel')}
        onConfirm={handleConfirmOwnerChange}
        onCancel={() => setPendingOwnerChange(null)}
      />

      <Snackbar
        open={!!actionError}
        autoHideDuration={6000}
        onClose={() => setActionError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setActionError(null)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {actionError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudioMembersTab;
